/**
 * Pure payload flattening for the node-properties panel: which fields a user may see, and how a step
 * artifact becomes readable "Label: value" lines.
 *
 * Extracted from `NodePropertiesPanel.tsx` so it can be tested WITHOUT rendering the panel — the
 * component's module graph reaches the board shapes, and a pure decision about what a plant engineer is
 * shown deserves a direct test rather than one routed through a render.
 */

export type ReadableRow = { label: string; text: string }

/** snake_case / camelCase field name → a "Title Case" label a user can read. */
export function humanizeKey(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase())
}

/**
 * Engine-internal fields that mean nothing to a user — dropped from the
 * readable view. (`instruction` is special-cased: its human preamble renders
 * as prose via `instructionProse`, the scaffolding stays hidden.)
 *
 * The DEGRADED-FALLBACK sentinels are here because they were reaching the user as
 * findings. Live run c9ba23a7 rendered `Historian Evidence · Workflow Tool Quota Stop:
 * Yes` — a flag the runner sets to mark that IT substituted a fallback artifact, shown
 * to a plant engineer as though it were something about their plant. The sentinels stay
 * ON the data (the step pill reads them via `displayStatusFor` to grade the step
 * honestly); they are only kept out of the readable field list.
 *
 * `readableRows` tests this set at EVERY level it walks, which is what makes it cover
 * the nested case: these flags arrive on an UPSTREAM step's artifact embedded inside a
 * later step's output, so a top-level-only filter would have missed exactly the
 * occurrence that shipped.
 */
export const DEGRADED_ARTIFACT_SENTINELS = [
  'workflowToolQuotaStop',
  'softTimeGuardReached',
  'kgPreconditionBlocked',
  'modelUnavailable',
  'stepErrorContinued',
] as const

/**
 * The reason STRING each sentinel above may bring with it (`workflow-synthesis.ts` stamps the pair).
 * Separate from the flags because `workflowGraph.ts` grades a step on the FLAGS only — a reason with no
 * flag is not a degradation — while the panel must hide both: "Step Error Reason: [PROVIDERS] provider=…"
 * is engine text, not a finding.
 */
const DEGRADED_REASON_FIELDS = [
  'kgPreconditionReason',
  'modelUnavailableReason',
  'stepErrorReason',
] as const

export const HIDDEN_FIELDS = new Set<string>([
  'instruction',
  'stepId',
  'outputSchemaValidation',
  // Degraded-fallback sentinels + their reason strings (workflow-synthesis.ts). The FLAG list is
  // exported and consumed by `workflowGraph.ts`'s step grading, so the two cannot drift — a fifth
  // brake added to one file is added to both. It is the twin of the backend's `classifyFallbackOutput`
  // (`workflow-ai-events.ts`), which is where a new sentinel is born.
  ...DEGRADED_ARTIFACT_SENTINELS,
  ...DEGRADED_REASON_FIELDS,
])

/** Engine scratch keys are `__`-prefixed by convention (`__verifyDemotion`). Matched by
 *  shape rather than listed, so a new one cannot leak into the panel by being forgotten. */
const isInternalKey = (key: string): boolean => key.startsWith('__')

/**
 * The human part of a step's `instruction` input. The engine composes it as
 * "Workflow: … / Step: … / <what this step does>" followed by internal
 * scaffolding (grounding rules, the persisted-state JSON dump) — everything
 * from the first scaffolding marker onward is cut.
 */
export function instructionProse(value: unknown): string {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return ''
  const instruction = (value as Record<string, unknown>).instruction
  if (typeof instruction !== 'string') return ''
  const cut = instruction.search(/\n+(Grounding rules:|Current persisted workflow state:)/)
  return (cut >= 0 ? instruction.slice(0, cut) : instruction).trim()
}

/**
 * Flatten a step payload into "Label: value" lines a plant engineer can read —
 * scalars verbatim, booleans as Yes/No, arrays as item counts, nested objects
 * flattened one level ("Parent · Child").
 */
export function readableRows(value: unknown, prefix = ''): ReadableRow[] {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return []
  const rows: ReadableRow[] = []
  for (const [key, raw] of Object.entries(value)) {
    if (rows.length >= 12) break
    if (HIDDEN_FIELDS.has(key) || isInternalKey(key)) continue
    if (raw === null || raw === undefined || raw === '') continue
    const label = prefix ? `${prefix} · ${humanizeKey(key)}` : humanizeKey(key)
    if (Array.isArray(raw)) {
      if (raw.length) rows.push({ label, text: `${raw.length} ${raw.length === 1 ? 'item' : 'items'}` })
    } else if (typeof raw === 'object') {
      // Cap BEFORE spreading — a nested object near the limit could otherwise
      // blow past it (11 parent rows + 12 nested = 23).
      if (!prefix) {
        const nested = readableRows(raw, humanizeKey(key))
        rows.push(...nested.slice(0, Math.max(0, 12 - rows.length)))
      }
    } else if (typeof raw === 'boolean') {
      rows.push({ label, text: raw ? 'Yes' : 'No' })
    } else {
      rows.push({ label, text: String(raw) })
    }
  }
  return rows
}

/**
 * Step artifacts often arrive as single-key wrapper chains
 * (`outputs → <outputKey> → outputs → <stepName> → {reason, status, …}`) —
 * descend to the payload that actually carries fields, so the readable view
 * never collapses to zero rows (which used to fall back to raw JSON).
 */
export function unwrapPayload(value: unknown): unknown {
  let current = value
  for (let hops = 0; hops < 5; hops += 1) {
    if (typeof current !== 'object' || current === null || Array.isArray(current)) break
    // The SAME filter `readableRows` applies, `__`-scratch included: a wrapper whose only other key is
    // engine bookkeeping must still unwrap, or the panel shows a one-row "Verify Demotion" instead of
    // the payload underneath it.
    const keys = Object.keys(current as Record<string, unknown>).filter(
      (key) => !HIDDEN_FIELDS.has(key) && !isInternalKey(key)
    )
    if (keys.length !== 1) break
    const inner = (current as Record<string, unknown>)[keys[0]]
    if (typeof inner !== 'object' || inner === null || Array.isArray(inner)) break
    current = inner
  }
  return current
}

/** blockedClaims / clarificationRequests entries as sentences, not "N items". */
export function specialLines(value: unknown): string[] {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return []
  const record = value as Record<string, unknown>
  const lines: string[] = []
  if (Array.isArray(record.clarificationRequests)) {
    for (const raw of record.clarificationRequests.slice(0, 3)) {
      const question = (raw as { question?: unknown })?.question
      if (typeof question === 'string') lines.push(`Asked the user: ${question}`)
    }
  }
  if (Array.isArray(record.blockedClaims)) {
    for (const raw of record.blockedClaims.slice(0, 5)) {
      // Entries are normally {claim, blockedBy} objects, but models sometimes
      // emit plain strings — those must still render, not silently vanish.
      //
      // `blockedBy` FIRST, `reason` as the fallback: that is the canonical schema
      // (`normalizeBlockedClaims`, which itself reads `blockedBy ?? reason`) and what every
      // RUNNER-written claim carries — `fallbackEvidence`, `blockedClaimsForUnmetEvidence`,
      // `workflow-clarification`, and the prescribed path's dropped-scope claim. Reading only
      // `reason` dropped the explanation from all of them, rendering a bare "Could not establish: …"
      // while `WorkflowResultPanel` showed the same claim WITH its cause — two panels disagreeing
      // about one record. `reason` stays as the fallback because a model-emitted claim may use it.
      const claim = typeof raw === 'string' ? raw : (raw as { claim?: unknown })?.claim
      const why =
        typeof raw === 'string'
          ? undefined
          : ((raw as { blockedBy?: unknown })?.blockedBy ?? (raw as { reason?: unknown })?.reason)
      if (typeof claim === 'string' && claim.trim()) {
        lines.push(`Could not establish: ${claim}${typeof why === 'string' ? ` — ${why}` : ''}`)
      }
    }
    if (record.blockedClaims.length > 5) {
      lines.push(`…and ${record.blockedClaims.length - 5} more open item(s)`)
    }
  }
  return lines
}
